import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Vendor } from '@models/vendor';
import { Product } from '@models/product';
import { VendorOrder } from '@models/vendor-order';
import { VendorService } from '@services/vendor.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { OrderService } from '@services/order.service';
import {
  STATUS_RED,
  STATUS_YELLOW,
  getAgingGradientColors,
} from '@classes/inventory-aging';

/** Inventory status green used across inventory UI. */
const STATUS_GREEN = '#04cc3a';

/**
 * Aging pie buckets align with the analytics mockup (<3 / 3–6 / 6–12 / >12 mo)
 * and reuse inventory status traffic-light colors (green → yellow → orange → red).
 */
const AGING_BUCKETS = [
  { key: 'under3', label: '< 3 mo', maxMonths: 3, color: STATUS_GREEN },
  {
    key: 'm3to6',
    label: '3-6 mo',
    maxMonths: 6,
    color: STATUS_YELLOW,
  },
  {
    key: 'm6to12',
    label: '6-12 mo',
    maxMonths: 12,
    color: getAgingGradientColors(5)[2] || '#ff7a55',
  },
  { key: 'over12', label: '> 12 mo', maxMonths: Infinity, color: STATUS_RED },
] as const;

const MONTH_OPTIONS = [
  { label: 'January', value: 0 },
  { label: 'February', value: 1 },
  { label: 'March', value: 2 },
  { label: 'April', value: 3 },
  { label: 'May', value: 4 },
  { label: 'June', value: 5 },
  { label: 'July', value: 6 },
  { label: 'August', value: 7 },
  { label: 'September', value: 8 },
  { label: 'October', value: 9 },
  { label: 'November', value: 10 },
  { label: 'December', value: 11 },
];

/**
 * Letter-grade thresholds (fulfillment / sell-through / overall):
 * A: >= 90, A-: >= 85, B+: >= 80, B: >= 75, B-: >= 70,
 * C+: >= 65, C: >= 60, C-: >= 55, D: >= 45, else F.
 * Fulfillment score: on-time % weighted with lead-time ratio.
 * Sell-through score: rate * 100 (capped).
 */
function scoreToLetter(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 85) return 'A-';
  if (score >= 80) return 'B+';
  if (score >= 75) return 'B';
  if (score >= 70) return 'B-';
  if (score >= 65) return 'C+';
  if (score >= 60) return 'C';
  if (score >= 55) return 'C-';
  if (score >= 45) return 'D';
  return 'F';
}

export interface AgingSlice {
  key: string;
  label: string;
  color: string;
  count: number;
  percent: number;
  dashArray: string;
  dashOffset: number;
}

export interface HealthMetric {
  label: string;
  value: string;
  barPercent: number;
  color: string;
}

@Component({
  selector: 'app-vendor-analytics',
  templateUrl: './vendor-analytics.component.html',
  styleUrls: ['./vendor-analytics.component.css'],
})
export class VendorAnalyticsComponent implements OnChanges, OnDestroy {
  @Input() vendor: Vendor;
  /** When false, skip loading until the expansion panel opens. */
  @Input() active = false;

  loading = false;
  animateIn = false;

  monthOptions = MONTH_OPTIONS;
  yearOptions: number[] = [];
  selectedMonth: number;
  selectedYear: number;

  // Monthly sales (invoice totals for selected month)
  monthlySales = 0;
  priorYearSales = 0;
  yoyPercent = 0;
  daysRemaining = 0;
  salesProgressPercent = 0;
  displayedSales = 0;

  // Health (single-vendor)
  healthMetrics: HealthMetric[] = [];
  sellThroughRate = 0;
  avgAgeDays = 0;
  returnsPercent = 0;

  // Inventory aging
  framesOnHand = 0;
  agingSlices: AgingSlice[] = [];
  donutCircumference = 2 * Math.PI * 54;

  // Grades
  overallGrade = '—';
  fulfillmentGrade = '—';
  sellThroughGrade = '—';

  // Shipping performance row
  shippingRow = {
    vendorName: '',
    avgFulfillmentDays: 0,
    expectedLeadTime: 30,
    onTimePercent: 0,
    partialPercent: 0,
    unfulfilledPercent: 0,
    grade: '—',
  };

  // Overview
  overview = {
    frameCount: 0,
    awaitingFrames: 0,
    frameReturns: 0,
    frameValue: 0,
    awaitingValue: 0,
    returnValue: 0,
    retailValue: 0,
    retailAwaiting: 0,
    returnLoss: 0,
  };

  private products: Product[] = [];
  private vendorOrders: VendorOrder[] = [];
  private sub: Subscription;
  private loadedVendorId: string = null;
  private countUpHandle: number = null;

  constructor(
    private vendorService: VendorService,
    private vendorOrderService: VendorOrderService,
    private orderService: OrderService
  ) {
    const now = new Date();
    this.selectedMonth = now.getMonth();
    this.selectedYear = now.getFullYear();
    for (let y = now.getFullYear(); y >= now.getFullYear() - 6; y--) {
      this.yearOptions.push(y);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vendor'] && this.vendor?._id !== this.loadedVendorId) {
      this.loadedVendorId = null;
    }
    if (this.active && this.vendor?._id && this.vendor._id !== this.loadedVendorId) {
      this.loadData();
    } else if (this.active && this.loadedVendorId) {
      this.triggerAnimations();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.countUpHandle != null) {
      cancelAnimationFrame(this.countUpHandle);
    }
  }

  onPeriodChange(): void {
    this.recomputeMonthlySales();
    this.triggerAnimations();
  }

  private loadData(): void {
    if (!this.vendor?._id) {
      return;
    }
    this.loading = true;
    this.animateIn = false;
    this.sub?.unsubscribe();

    this.sub = forkJoin({
      inventory: this.vendorService
        .getVendorInventory(this.vendor, '', 'all')
        .pipe(catchError(() => of([]))),
      invoices: this.vendorOrderService
        .getVendorVendorOrders('all', this.vendor._id)
        .pipe(catchError(() => of({ vendorOrders: [] } as any))),
      awaiting: this.orderService
        .getNewOrders('awaiting-frames')
        .pipe(catchError(() => of([]))),
      returns: this.orderService
        .getNewOrders('returns')
        .pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ inventory, invoices, awaiting, returns }) => {
        const inv = Array.isArray(inventory) ? inventory[0] : null;
        this.products = inv?.products || [];
        this.vendorOrders =
          invoices?.vendorOrders?.[0]?.vendorOrders ||
          [];
        if (!Array.isArray(this.vendorOrders)) {
          this.vendorOrders = [];
        }

        const productIds = this.products
          .map((p) => p._id)
          .filter(Boolean) as string[];

        const finish = () => {
          this.computeInventoryMetrics();
          this.computeShippingPerformance();
          this.computeOverview(awaiting || [], returns || []);
          this.recomputeMonthlySales();
          this.computeHealthAndGrades();
          this.loadedVendorId = this.vendor._id;
          this.loading = false;
          this.triggerAnimations();
        };

        if (!productIds.length) {
          finish();
          return;
        }

        // Reuse same last-invoice-date source as inventory aging UI
        this.vendorService
          .getProductLastInvoiceDates(productIds)
          .pipe(catchError(() => of({} as Record<string, string>)))
          .subscribe((dates) => {
            for (const p of this.products) {
              if (dates?.[p._id]) {
                p.lastInvoiceDate = dates[p._id];
              }
            }
            finish();
          });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private triggerAnimations(): void {
    this.animateIn = false;
    // Restart CSS animations on next frame
    requestAnimationFrame(() => {
      this.animateIn = true;
      this.runCountUp();
    });
  }

  private runCountUp(): void {
    if (this.countUpHandle != null) {
      cancelAnimationFrame(this.countUpHandle);
    }
    const target = this.monthlySales;
    const start = performance.now();
    const duration = 450;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.displayedSales = target * eased;
      if (t < 1) {
        this.countUpHandle = requestAnimationFrame(step);
      } else {
        this.displayedSales = target;
        this.countUpHandle = null;
      }
    };
    this.countUpHandle = requestAnimationFrame(step);
  }

  private recomputeMonthlySales(): void {
    this.monthlySales = this.sumInvoicesForMonth(
      this.selectedYear,
      this.selectedMonth
    );
    this.priorYearSales = this.sumInvoicesForMonth(
      this.selectedYear - 1,
      this.selectedMonth
    );

    if (this.priorYearSales > 0) {
      this.yoyPercent =
        ((this.monthlySales - this.priorYearSales) / this.priorYearSales) * 100;
      this.salesProgressPercent = Math.min(
        200,
        (this.monthlySales / this.priorYearSales) * 100
      );
    } else {
      this.yoyPercent = this.monthlySales > 0 ? 100 : 0;
      this.salesProgressPercent = this.monthlySales > 0 ? 100 : 0;
    }

    const now = new Date();
    const isCurrentPeriod =
      this.selectedYear === now.getFullYear() &&
      this.selectedMonth === now.getMonth();
    if (isCurrentPeriod) {
      const lastDay = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0
      ).getDate();
      this.daysRemaining = Math.max(0, lastDay - now.getDate());
    } else {
      this.daysRemaining = 0;
    }
  }

  /** Monthly sales approximated from vendor invoice totals for the period. */
  private sumInvoicesForMonth(year: number, month: number): number {
    return this.vendorOrders.reduce((sum, vo) => {
      const d = this.parseDate(vo.date || vo.dateAdded);
      if (!d || d.getFullYear() !== year || d.getMonth() !== month) {
        return sum;
      }
      return sum + (Number(vo.total) || 0);
    }, 0);
  }

  private computeInventoryMetrics(): void {
    const onHand = this.products.filter((p) => (Number(p.quantity) || 0) > 0);
    this.framesOnHand = onHand.reduce(
      (n, p) => n + (Number(p.quantity) || 0),
      0
    );

    const bucketCounts: Record<string, number> = {
      under3: 0,
      m3to6: 0,
      m6to12: 0,
      over12: 0,
    };
    let totalAgeDays = 0;
    let ageWeight = 0;

    for (const p of onHand) {
      const qty = Number(p.quantity) || 0;
      const months = this.monthsSinceInvoice(p);
      const days = months * 30.44;
      totalAgeDays += days * qty;
      ageWeight += qty;

      if (months < 3) bucketCounts.under3 += qty;
      else if (months < 6) bucketCounts.m3to6 += qty;
      else if (months < 12) bucketCounts.m6to12 += qty;
      else bucketCounts.over12 += qty;
    }

    this.avgAgeDays = ageWeight > 0 ? Math.round(totalAgeDays / ageWeight) : 0;

    const total = this.framesOnHand || 1;
    let offset = 0;
    this.agingSlices = AGING_BUCKETS.map((b) => {
      const count = bucketCounts[b.key] || 0;
      const percent = this.framesOnHand
        ? Math.round((count / total) * 1000) / 10
        : 0;
      const length = (count / total) * this.donutCircumference;
      const slice: AgingSlice = {
        key: b.key,
        label: b.label,
        color: b.color,
        count,
        percent,
        dashArray: `${length} ${this.donutCircumference}`,
        dashOffset: -offset,
      };
      offset += length;
      return slice;
    });

    this.overview.frameCount = this.framesOnHand;
    this.overview.frameValue = onHand.reduce(
      (s, p) => s + (Number(p.cost) || 0) * (Number(p.quantity) || 0),
      0
    );
    this.overview.retailValue = onHand.reduce(
      (s, p) => s + (Number(p.retail) || 0) * (Number(p.quantity) || 0),
      0
    );
  }

  private monthsSinceInvoice(product: Product): number {
    const raw = product.lastInvoiceDate || product.dateAdded || product.orderDate;
    const d = this.parseDate(raw);
    if (!d) {
      return 0;
    }
    const now = new Date();
    const months =
      (now.getFullYear() - d.getFullYear()) * 12 +
      (now.getMonth() - d.getMonth()) +
      (now.getDate() - d.getDate()) / 30;
    return Math.max(0, months);
  }

  private computeShippingPerformance(): void {
    // No dedicated expected-lead-time field on Vendor; default 30 days (mockup).
    // shippingMin is a min order qty, not days — do not use it here.
    const expected = 30;
    this.shippingRow.vendorName = this.vendor?.name || '';
    this.shippingRow.expectedLeadTime = expected;

    const withReceived = this.vendorOrders.filter((vo) => {
      const start = this.parseDate(vo.date || vo.dateAdded);
      const end = this.parseDate(vo.dateReceived);
      return start && end && end >= start;
    });

    const fulfillmentDays = withReceived.map((vo) => {
      const start = this.parseDate(vo.date || vo.dateAdded).getTime();
      const end = this.parseDate(vo.dateReceived).getTime();
      return Math.max(0, (end - start) / (1000 * 60 * 60 * 24));
    });

    this.shippingRow.avgFulfillmentDays = fulfillmentDays.length
      ? Math.round(
          (fulfillmentDays.reduce((a, b) => a + b, 0) / fulfillmentDays.length) *
            10
        ) / 10
      : 0;

    const onTime = fulfillmentDays.filter((d) => d <= expected).length;
    this.shippingRow.onTimePercent = fulfillmentDays.length
      ? Math.round((onTime / fulfillmentDays.length) * 1000) / 10
      : 0;

    const total = this.vendorOrders.length || 1;
    const partial = this.vendorOrders.filter(
      (vo) =>
        (Number(vo.partialPayment) || 0) > 0 ||
        String(vo.status || '').toUpperCase() === 'CREDITED'
    ).length;
    const unfulfilled = this.vendorOrders.filter((vo) => {
      const status = String(vo.status || '').toUpperCase();
      return (
        !vo.dateReceived &&
        !['PAID', 'RECEIVED', 'RETURNED', 'CREDITED', 'SOLD'].includes(status)
      );
    }).length;

    this.shippingRow.partialPercent =
      Math.round((partial / total) * 1000) / 10;
    this.shippingRow.unfulfilledPercent =
      Math.round((unfulfilled / total) * 1000) / 10;

    // Fulfillment score: on-time % with penalty when avg days exceed lead time
    const leadRatio =
      this.shippingRow.avgFulfillmentDays > 0
        ? Math.min(
            1,
            expected / Math.max(this.shippingRow.avgFulfillmentDays, 1)
          )
        : this.shippingRow.onTimePercent > 0
        ? 1
        : 0;
    const fulfillmentScore =
      this.shippingRow.onTimePercent * 0.7 + leadRatio * 100 * 0.3;
    this.fulfillmentGrade = scoreToLetter(fulfillmentScore);
    this.shippingRow.grade = this.fulfillmentGrade;
  }

  private computeOverview(awaitingGroups: any[], returnGroups: any[]): void {
    const vendorId = this.vendor._id;
    const matchVendor = (group: any) => {
      const id = group?._id || group?.vendor?._id || group?.vendor;
      return String(id) === String(vendorId);
    };

    const awaitingGroup = (awaitingGroups || []).find(matchVendor);
    const returnGroup = (returnGroups || []).find(matchVendor);

    const awaitingOrders: any[] = awaitingGroup?.orders || [];
    const returnOrders: any[] =
      returnGroup?.orders || returnGroup?.vendorOrders || [];

    let awaitingFrames = 0;
    let awaitingCost = 0;
    let awaitingRetail = 0;
    for (const order of awaitingOrders) {
      const products = order?.products || [];
      for (const pe of products) {
        const p = pe?.product || pe;
        awaitingFrames += 1;
        awaitingCost += Number(p?.cost) || 0;
        awaitingRetail += Number(p?.retail) || Number(pe?.price) || 0;
      }
    }

    let frameReturns = 0;
    let returnCost = 0;
    let returnShipping = 0;
    let returnCreditLoss = 0;

    if (returnGroup?.vendorOrders?.length) {
      for (const vo of returnGroup.vendorOrders) {
        const products = vo?.products || [];
        frameReturns += products.length || 0;
        for (const pe of products) {
          const p = pe?.product || pe;
          returnCost += Number(p?.cost) || Number(pe?.cost) || 0;
        }
        returnShipping += Number(vo?.shipping) || 0;
        const proposed = Number(vo?.proposedCredit) || 0;
        const credit = Number(vo?.credit) || 0;
        if (proposed > credit) {
          returnCreditLoss += proposed - credit;
        }
      }
    } else {
      for (const order of returnOrders) {
        const products = order?.products || [];
        frameReturns += products.length || 0;
        for (const pe of products) {
          const p = pe?.product || pe;
          returnCost += Number(p?.cost) || 0;
        }
      }
    }

    // Also count RETURN-status products still in inventory if board lists are empty
    if (frameReturns === 0) {
      const returnProducts = this.products.filter(
        (p) =>
          String(p.status || '').toLowerCase() === 'return' ||
          String(p.status || '').toLowerCase() === 'returning' ||
          p.return === true
      );
      frameReturns = returnProducts.reduce(
        (n, p) => n + Math.max(1, Number(p.quantity) || 0),
        0
      );
      returnCost = returnProducts.reduce(
        (s, p) => s + (Number(p.cost) || 0) * Math.max(1, Number(p.quantity) || 0),
        0
      );
    }

    this.overview.awaitingFrames = awaitingFrames;
    this.overview.awaitingValue = awaitingCost;
    this.overview.retailAwaiting = awaitingRetail;
    this.overview.frameReturns = frameReturns;
    this.overview.returnValue = returnCost;
    // Return loss: shipping on return invoices + shortfall vs proposed credit (best available)
    this.overview.returnLoss = returnShipping + returnCreditLoss;

    const soldUnits = this.products.reduce(
      (n, p) => n + (Number(p.sold) || 0),
      0
    );
    this.returnsPercent =
      soldUnits + frameReturns > 0
        ? Math.round((frameReturns / (soldUnits + frameReturns)) * 1000) / 10
        : 0;
  }

  private computeHealthAndGrades(): void {
    const sold = this.products.reduce((n, p) => n + (Number(p.sold) || 0), 0);
    const onHand = this.framesOnHand;
    this.sellThroughRate =
      sold + onHand > 0
        ? Math.round((sold / (sold + onHand)) * 1000) / 10
        : 0;

    const sellThroughScore = Math.min(100, this.sellThroughRate);
    this.sellThroughGrade = scoreToLetter(sellThroughScore);

    const fulfillmentScoreMap: Record<string, number> = {
      A: 92,
      'A-': 87,
      'B+': 82,
      B: 77,
      'B-': 72,
      'C+': 67,
      C: 62,
      'C-': 57,
      D: 48,
      F: 30,
      '—': 0,
    };
    const fScore = fulfillmentScoreMap[this.fulfillmentGrade] ?? 0;
    const overall = fScore * 0.55 + sellThroughScore * 0.45;
    this.overallGrade = scoreToLetter(overall);

    // Single-vendor health bars (not multi-vendor comparison on this page)
    this.healthMetrics = [
      {
        label: 'Sell-Through Rate',
        value: `${this.sellThroughRate}%`,
        barPercent: Math.min(100, this.sellThroughRate),
        color: this.rateColor(this.sellThroughRate, true),
      },
      {
        label: 'Avg Inventory Age',
        value: `${this.avgAgeDays} days`,
        barPercent: Math.min(100, (this.avgAgeDays / 365) * 100),
        color: this.rateColor(100 - Math.min(100, (this.avgAgeDays / 365) * 100), true),
      },
      {
        label: 'Returns',
        value: `${this.returnsPercent}%`,
        barPercent: Math.min(100, this.returnsPercent * 2),
        color: this.rateColor(100 - Math.min(100, this.returnsPercent * 4), true),
      },
    ];
  }

  private rateColor(score: number, higherIsBetter: boolean): string {
    const s = higherIsBetter ? score : 100 - score;
    if (s >= 55) return STATUS_GREEN;
    if (s >= 35) return STATUS_YELLOW;
    return STATUS_RED;
  }

  private parseDate(value: any): Date | null {
    if (value == null || value === '') {
      return null;
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  getDashArray(slice: AgingSlice): string {
    return slice.dashArray || `0 ${this.donutCircumference}`;
  }

  get radialDashArray(): string {
    const pct = Math.min(100, this.salesProgressPercent) / 100;
    const circ = 2 * Math.PI * 48;
    return `${pct * circ} ${circ}`;
  }

  get radialCircumference(): number {
    return 2 * Math.PI * 48;
  }
}
